import { useCallback, useEffect } from "react";

import useDailyReadContext from "@app/analytics/hooks/useDailyReadContext";
import useDailyReadStore from "@app/analytics/stores/useDailyReadStore";

const useAudioControls = ({
  id,
  src,
  displayOnly,
  onPlayed,
}: {
  id: string;
  src?: string;
  displayOnly: boolean;
  onPlayed?: () => void;
}) => {
  const { lastMinimizedId, lastListenedId, lastPlayedId } =
    useDailyReadContext();
  const store = useDailyReadStore();

  const audioEl = store.audioEl;

  const shouldLoadAudio = Boolean(src) && !displayOnly;

  useEffect(() => {
    if (id === store.id) {
      store.setAudioEl(shouldLoadAudio ? new Audio(src) : null);
    } else {
      store.initializeDailyRead({
        id,
        audioEl: shouldLoadAudio ? new Audio(src) : null,
        minimized: lastMinimizedId === id,
        status:
          lastListenedId === id
            ? "listened"
            : lastPlayedId === id
              ? null
              : "new",
      });
    }
  }, [shouldLoadAudio, id]);

  const handleScrubberChange = useCallback(
    (seconds: number) => {
      if (!audioEl) return;
      audioEl.currentTime = seconds;
      store.setCurrentTimestamp(seconds);
    },
    [audioEl]
  );

  const handleTimeUpdate = useCallback(() => {
    if (!audioEl) return;
    const hasEnded = audioEl.currentTime >= audioEl.duration;
    store.setCurrentTimestamp(audioEl.currentTime, hasEnded);
    if (hasEnded) {
      onPlayed?.();
    }
  }, [audioEl]);

  const handleDurationUpdate = useCallback(() => {
    if (!audioEl || isNaN(audioEl.duration)) return;
    store.setDuration(audioEl.duration);
  }, [audioEl]);

  useEffect(() => {
    if (!audioEl || displayOnly) return;
    audioEl.addEventListener("timeupdate", handleTimeUpdate);
    return () => audioEl.removeEventListener("timeupdate", handleTimeUpdate);
  }, [handleTimeUpdate, displayOnly]);

  useEffect(() => {
    if (!audioEl || displayOnly) return;
    audioEl.addEventListener("durationchange", handleDurationUpdate);
    return () =>
      audioEl.removeEventListener("durationchange", handleDurationUpdate);
  }, [handleDurationUpdate, displayOnly]);

  return {
    duration: store.duration,
    play: store.play,
    pause: store.pause,
    skipBackward: store.skipBackward,
    skipForward: store.skipForward,
    mute: store.mute,
    unmute: store.unmute,
    onScrubberChange: handleScrubberChange,
  };
};

export default useAudioControls;
